import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Video } from '@/types';
import { videoTypeLabels } from '@/mocks/videos';

interface HeroCarouselProps {
  items: Video[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const count = items.length;

  const go = useCallback(
    (next: number) => {
      setIndex((next + count) % count);
    },
    [count],
  );

  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % count);
    }, 6000);
    return () => clearInterval(timer);
  }, [count]);

  if (count === 0) return null;
  const active = items[index];

  return (
    <section className="relative h-[420px] w-full overflow-hidden md:h-[560px]">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照`}
            title={`${item.title} 精彩剧照`}
            className={`h-full w-full object-cover object-top ${i === index ? 'animate-kenburns' : ''}`}
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/70 to-background-50/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-background-50/40" />

      <div className="relative mx-auto flex h-full w-full max-w-[1280px] flex-col justify-center px-4 md:px-8">
        <div key={active.id} className="max-w-xl animate-fade-up">
          <div className="mb-4 flex items-center gap-3">
            <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-semibold text-background-50">
              {videoTypeLabels[active.type]}
            </span>
            <span className="flex items-center gap-1 text-sm font-semibold text-accent-500">
              <i className="ri-star-fill" />
              {active.rating.toFixed(1)}
            </span>
            <span className="text-sm text-foreground-400">{active.year}</span>
          </div>
          <h1 className="text-3xl font-black leading-tight text-foreground-50 md:text-5xl">
            {active.title}
          </h1>
          <p className="mt-4 line-clamp-2 max-w-lg text-sm leading-relaxed text-foreground-300 md:text-base">
            {active.synopsis}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${active.id}`}
              className="flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-play-fill text-lg" />
              立即观看
            </Link>
            <Link
              to={`/category/${active.type}`}
              className="rounded-md border border-background-300 bg-background-50/50 px-5 py-2.5 text-sm font-medium text-foreground-100 backdrop-blur-sm transition-colors hover:border-primary-400 hover:text-primary-500"
            >
              更多{videoTypeLabels[active.type]}
            </Link>
          </div>
        </div>

        <div className="absolute bottom-6 right-4 flex items-center gap-3 md:right-8">
          <button
            type="button"
            onClick={() => go(index - 1)}
            aria-label="上一张"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-300 bg-background-50/50 text-foreground-100 backdrop-blur-sm transition-colors hover:border-primary-400 hover:text-primary-500"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <button
            type="button"
            onClick={() => go(index + 1)}
            aria-label="下一张"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-300 bg-background-50/50 text-foreground-100 backdrop-blur-sm transition-colors hover:border-primary-400 hover:text-primary-500"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
          <div className="flex items-center gap-1.5">
            {items.map((item, i) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`切换到 ${item.title}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === index ? 'w-6 bg-primary-500' : 'w-1.5 bg-background-400'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}